import { useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/layout/AdminLayout";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Download,
  Upload,
  FileSpreadsheet,
  CheckCircle,
  AlertCircle,
  Info,
  ArrowLeft,
  ArrowRight,
  RotateCcw,
} from "lucide-react";
import {
  ImportModelType,
  ImportPreviewData,
  ImportConfig,
} from "@/types/import";
import { downloadTemplate } from "@/utils/import-helpers/excel-generator";
import { importAPI } from "@/api/import";
import ImportWizard from "@/components/data-import/ImportWizard";
import ImportHistory from "@/components/data-import/ImportHistory";
import { useRef } from "react";

type ImportStep =
  | "select-model"
  | "download-template"
  | "upload-file"
  | "preview-data"
  | "import"
  | "complete";

const DataImport = () => {
  const [currentStep, setCurrentStep] = useState<ImportStep>("select-model");
  const [selectedModel, setSelectedModel] = useState<ImportModelType | null>(
    null
  );
  const [uploadedFile, setUploadedFile] = useState<File | null>(null);
  const [previewData, setPreviewData] = useState<ImportPreviewData | null>(
    null
  );
  const [importConfig, setImportConfig] = useState<ImportConfig>({
    batchSize: 500,
    allowPartialSuccess: true,
    createMissingForeignKeys: false,
    duplicateStrategy: "skip",
    validateOnly: false,
  });
  const [activeTab, setActiveTab] = useState<"import" | "history">("import");
  const importHistoryRef = useRef<{ refreshHistory: () => void } | null>(null);

  const { toast } = useToast();
  const navigate = useNavigate();

  const modelConfigs = [
    {
      type: "categories" as ImportModelType,
      title: "Categories",
      description: "Import book categories with names, icons, and positions",
      icon: "📚",
      fields: ["Name", "Name (Mizo)", "Icon", "Position", "Is Active"],
    },
    {
      type: "people" as ImportModelType,
      title: "People",
      description: "Import authors, narrators, translators and other people",
      icon: "👥",
      fields: ["Name", "Email", "Bio", "Avatar URL", "Roles"],
    },
    {
      type: "books" as ImportModelType,
      title: "Books",
      description: "Import book summaries with metadata and relationships",
      icon: "📖",
      fields: [
        "Title",
        "Cover Image",
        "Reading Time",
        "Rating",
        "Authors",
        "Categories",
      ],
    },
    {
      type: "summary_contents" as ImportModelType,
      title: "Summary Contents",
      description: "Import summary content for different languages",
      icon: "📄",
      fields: [
        "Book Title",
        "Language",
        "Subtitle",
        "Description",
        "Audio Info",
      ],
    },
    {
      type: "chapters" as ImportModelType,
      title: "Chapters",
      description: "Import chapters for book summaries",
      icon: "📝",
      fields: ["Book Title", "Language", "Label", "Title", "Content"],
    },
  ];

  const steps = [
    {
      key: "select-model",
      title: "Select Model",
      description: "Choose what to import",
    },
    {
      key: "download-template",
      title: "Get Template",
      description: "Download Excel template",
    },
    {
      key: "upload-file",
      title: "Upload File",
      description: "Upload your Excel file",
    },
    {
      key: "preview-data",
      title: "Preview & Validate",
      description: "Review your data",
    },
    { key: "import", title: "Import", description: "Process the import" },
    { key: "complete", title: "Complete", description: "View results" },
  ];

  const getCurrentStepIndex = () => {
    return steps.findIndex((step) => step.key === currentStep);
  };

  const handleModelSelect = (modelType: ImportModelType) => {
    setSelectedModel(modelType);
    setCurrentStep("download-template");
  };

  const handleDownloadTemplate = async () => {
    if (!selectedModel) return;

    try {
      await downloadTemplate(selectedModel);
      toast({
        title: "Template Downloaded",
        description: `Excel template for ${selectedModel} has been downloaded with real data examples. Fill it with your data and upload it in the next step.`,
      });
      setCurrentStep("upload-file");
    } catch (error) {
      toast({
        title: "Download Failed",
        description: "Failed to download template. Please try again.",
        variant: "destructive",
      });
    }
  };

  const handleReset = () => {
    setCurrentStep("select-model");
    setSelectedModel(null);
    setUploadedFile(null);
    setPreviewData(null);
    setImportConfig({
      batchSize: 500,
      allowPartialSuccess: true,
      createMissingForeignKeys: false,
      duplicateStrategy: "skip",
      validateOnly: false,
    });
  };

  const handleImportComplete = () => {
    // Refresh import history when an import completes
    if (importHistoryRef.current) {
      importHistoryRef.current.refreshHistory();
    }
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case "select-model":
        return (
          <div className="space-y-6">
            <div className="text-center">
              <h2 className="text-2xl font-bold mb-2">Choose What to Import</h2>
              <p className="text-muted-foreground">
                Select the type of data you want to import into the system
              </p>
            </div>

            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {modelConfigs.map((config) => (
                <Card
                  key={config.type}
                  className="cursor-pointer hover:shadow-md transition-all duration-200 hover:scale-105"
                  onClick={() => handleModelSelect(config.type)}
                >
                  <CardHeader className="text-center">
                    <div className="text-4xl mb-2">{config.icon}</div>
                    <CardTitle className="text-lg">{config.title}</CardTitle>
                    <CardDescription>{config.description}</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-2">
                      <p className="text-sm font-medium text-muted-foreground">
                        Key Fields:
                      </p>
                      <div className="flex flex-wrap gap-1">
                        {config.fields.slice(0, 3).map((field) => (
                          <Badge
                            key={field}
                            variant="secondary"
                            className="text-xs"
                          >
                            {field}
                          </Badge>
                        ))}
                        {config.fields.length > 3 && (
                          <Badge variant="outline" className="text-xs">
                            +{config.fields.length - 3} more
                          </Badge>
                        )}
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        );

      case "download-template": {
        const currentConfig = modelConfigs.find(
          (c) => c.type === selectedModel
        );
        return (
          <div className="space-y-6 text-center max-w-2xl mx-auto">
            <div>
              <div className="text-4xl mb-4">{currentConfig?.icon}</div>
              <h2 className="text-2xl font-bold mb-2">
                Download Excel Template
              </h2>
              <p className="text-muted-foreground">
                Download the template for{" "}
                <strong>{currentConfig?.title}</strong> and fill it with your
                data
              </p>
            </div>

            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileSpreadsheet className="w-5 h-5" />
                  {currentConfig?.title} Template
                </CardTitle>
                <CardDescription>
                  This template includes sample data, validation rules, and
                  detailed instructions
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid gap-2 text-sm">
                  <div className="flex justify-between">
                    <span>Fields included:</span>
                    <span className="font-medium">
                      {currentConfig?.fields.length}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Sample data rows:</span>
                    <span className="font-medium">2-3</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Instructions sheet:</span>
                    <span className="font-medium">✓ Included</span>
                  </div>
                </div>

                <div className="space-y-3">
                  <Button
                    onClick={handleDownloadTemplate}
                    className="w-full"
                    size="lg"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>

                  <Button
                    onClick={() => setCurrentStep("upload-file")}
                    variant="outline"
                    className="w-full"
                    size="lg"
                  >
                    <ArrowRight className="w-4 h-4 mr-2" />
                    Skip & Continue
                  </Button>
                </div>
              </CardContent>
            </Card>

            <div className="bg-blue-50 dark:bg-blue-950/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5" />
                <div className="text-sm text-blue-800 dark:text-blue-200">
                  <p className="font-medium mb-1">Template Instructions:</p>
                  <ul className="space-y-1 text-left">
                    <li>• Fill in your data starting from row 2</li>
                    <li>• Leave ID column blank for new entries</li>
                    <li>
                      • Check the Instructions sheet for detailed guidance
                    </li>
                    <li>• Save the file and upload it in the next step</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        );
      }

      case "upload-file":
      case "preview-data":
      case "import":
      case "complete":
        return (
          <ImportWizard
            currentStep={currentStep}
            selectedModel={selectedModel!}
            uploadedFile={uploadedFile}
            previewData={previewData}
            importConfig={importConfig}
            onStepChange={setCurrentStep}
            onFileUpload={setUploadedFile}
            onPreviewData={setPreviewData}
            onConfigChange={setImportConfig}
            onComplete={handleReset}
            onViewHistory={() => setActiveTab("history")}
            onImportComplete={handleImportComplete}
          />
        );

      default:
        return <div>Unknown step</div>;
    }
  };

  return (
    <AdminLayout title="Data Import">
      <div className="space-y-6">
        <Tabs
          value={activeTab}
          onValueChange={(value) => {
            // If switching to import tab and we're on complete step but missing essential data, reset
            if (
              value === "import" &&
              currentStep === "complete" &&
              (!selectedModel || !uploadedFile || !previewData)
            ) {
              handleReset();
            }
            setActiveTab(value as "import" | "history");
          }}
        >
          <TabsList className="grid w-full grid-cols-2">
            <TabsTrigger value="import">Import Data</TabsTrigger>
            <TabsTrigger value="history">Import History</TabsTrigger>
          </TabsList>

          <TabsContent value="import" className="space-y-6">
            {/* Progress Header */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Upload className="w-5 h-5" />
                      Data Import Wizard
                    </CardTitle>
                    <CardDescription>
                      Step {getCurrentStepIndex() + 1} of {steps.length}:{" "}
                      {steps[getCurrentStepIndex()]?.title}
                    </CardDescription>
                  </div>

                  {currentStep !== "select-model" && (
                    <Button variant="outline" onClick={handleReset} size="sm">
                      <RotateCcw className="w-4 h-4 mr-2" />
                      Start Over
                    </Button>
                  )}
                </div>

                <div className="space-y-2">
                  <Progress
                    value={(getCurrentStepIndex() / (steps.length - 1)) * 100}
                    className="w-full"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    {steps.map((step, index) => (
                      <div
                        key={step.key}
                        className={`text-center ${
                          index <= getCurrentStepIndex() ? "text-primary" : ""
                        }`}
                      >
                        <div className="hidden sm:block">{step.title}</div>
                        <div className="sm:hidden">{index + 1}</div>
                      </div>
                    ))}
                  </div>
                </div>
              </CardHeader>
            </Card>

            {/* Step Content */}
            <div className="min-h-[500px]">{renderStepContent()}</div>
          </TabsContent>

          <TabsContent value="history">
            <ImportHistory ref={importHistoryRef} />
          </TabsContent>
        </Tabs>
      </div>
    </AdminLayout>
  );
};

export default DataImport;
